import { isTextMessageAccepted } from '../entities/message'
// useChatConnection — ports connection.js (WebSocket lifecycle, heartbeat,
// exponential-backoff reconnect, 30s polling fallback, online/offline).
//
// Returns { status, send } where send(content) returns true if it went out over
// an open socket (caller does the HTTP fallback otherwise). Server→client frames
// are routed into messagingStore; show_pricing/clear_chat actions are surfaced
// via callbacks.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { useMessagingStore } from '../store/messagingStore'
import { useChatwootStore } from '../store/chatwootStore'
import { chatApi, normalizeServerMessage } from '../api/chat'
import { toast } from '../store/toastStore'
import { WS_BASE } from '../lib/platform'
import { analytics } from '../lib/analytics'

const MAX_RETRIES = 10
const HEARTBEAT_MS = 30000
const POLL_MS = 30000

type ConnectionStatus = 'connecting' | 'connected' | 'offline' | 'failed'

export function useChatConnection({ enabled }: { enabled: boolean }) {
  const [status, setStatus] = useState<ConnectionStatus>('connecting')

  const wsRef = useRef<WebSocket | null>(null)
  const retryRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const closingRef = useRef(false) // intentional close (unmount) — skip reconnect
  const pendingAcceptance = useRef(0)

  const stopHeartbeat = () => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current)
    heartbeatRef.current = null
  }
  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = null
  }

  // Opportunistic poll — append assistant messages we don't already have.
  const pollNewMessages = useCallback(async () => {
    const { isLoading, addMessage, messages } = useMessagingStore.getState()
    if (isLoading) return
    try {
      const data = await chatApi.history(10)
      if (!data?.messages?.length) return
      // Dedup against only the recent tail, not the whole transcript, so a
      // legitimately-repeated reply isn't silently dropped (original matched on
      // timestamp±2s + content, connection.js:47-57; we approximate with a
      // recent-window since the store keeps only client-side timestamps).
      const have = new Set(messages.slice(-10).map((m) => m.content))
      for (const raw of data.messages) {
        if (raw.metadata?.role === 'assistant' && !have.has(raw.content)) {
          const n = normalizeServerMessage(raw, 'assistant')
          addMessage('assistant', n.content, { contentType: n.contentType, mediaUrl: n.mediaUrl, metadata: n.metadata })
        }
      }
    } catch {
      /* polling is best-effort */
    }
  }, [])

  const startPolling = useCallback(() => {
    stopPolling()
    pollRef.current = setInterval(pollNewMessages, POLL_MS)
  }, [pollNewMessages])

  const startHeartbeat = () => {
    stopHeartbeat()
    heartbeatRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }))
      }
    }, HEARTBEAT_MS)
  }

  const connect = useCallback(() => {
    const { userId, accessToken } = useAuthStore.getState()
    if (!userId || !accessToken) return

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }

    const url = `${WS_BASE}/api/v1/ws/${userId}?token=${encodeURIComponent(accessToken)}`
    setStatus('connecting')

    let ws: WebSocket
    try {
      ws = new WebSocket(url)
    } catch {
      setStatus('failed')
      return
    }
    wsRef.current = ws

    const store = useMessagingStore.getState

    ws.onopen = () => {
      const reconnected = retryRef.current > 0 // was dropped and came back
      retryRef.current = 0
      setStatus('connected')
      stopPolling()
      flushPending()
      startHeartbeat()
      // Resync the Chatwoot badge in case a push was missed offline (parent bd-eiw).
      useChatwootStore.getState().fetch()
      // Original toasted on connect (connection.js:96); only surface it on a
      // real reconnect so the first page load isn't noisy.
      if (reconnected) toast('Подключено к серверу', 'success')
    }

    ws.onmessage = (event) => {
      let data
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }
      const { addMessage, setTyping, setLoading, clear, messages, addPaywall, addLimitNotice } = store()

      if (data.type === 'welcome') {
        if (messages.length === 0 && data.message) addMessage('assistant', data.message)
      } else if (data.type === 'message') {
        setTyping(false)
        setLoading(false)
        if (data.metadata?.action === 'show_pricing') {
          pendingAcceptance.current = Math.max(0, pendingAcceptance.current - 1)
          // WS equivalent of a 402 — render the inline paywall in the transcript
          // (parent feat/inline-paywall) instead of switching to /pricing.
          addPaywall()
        } else if (data.metadata?.action === 'show_limit_notice') {
          pendingAcceptance.current = Math.max(0, pendingAcceptance.current - 1)
          // Subscribed user hit the weekly/token quota — friendly notice, no upsell.
          addLimitNotice(data.metadata.reset_at)
        } else if (data.metadata?.action === 'clear_chat') {
          clear()
        } else {
          addMessage('assistant', data.content, {
            contentType: data.content_type || 'text',
            mediaUrl: data.media_url || null,
            metadata: data.metadata ?? null,
          })
        }
      } else if (data.type === 'chatwoot_status') {
        // Support-conversation unread update (parent bd-eiw).
        useChatwootStore.getState().setStatus(data)
      } else if (data.type === 'typing') {
        // router.py sends exactly one typing:true after accepting each user
        // message and passing access checks. AI/OOB messages cannot trigger this.
        if (data.status === true && pendingAcceptance.current > 0) {
          pendingAcceptance.current -= 1
          analytics.track('messageAccepted')
        }
        setTyping(Boolean(data.status))
      } else if (data.type === 'error') {
        // Terminal frame for the turn — release the input lock, else the send
        // button stays disabled (isLoading stuck true) after a server error.
        setTyping(false)
        setLoading(false)
        toast(data.message || 'Ошибка сервера', 'error')
      }
    }

    ws.onclose = () => {
      pendingAcceptance.current = 0
      stopHeartbeat()
      if (closingRef.current) return // intentional (unmount) — don't reconnect
      // A turn in flight over this socket can't finish here — release the input
      // lock so the user isn't stuck; polling/HTTP will still deliver the reply.
      store().setLoading(false)
      store().setTyping(false)
      startPolling()
      if (retryRef.current < MAX_RETRIES) {
        const delay = Math.min(3000 * 2 ** retryRef.current, 30000)
        retryRef.current += 1
        setStatus('connecting')
        toast(`Соединение потеряно. Переподключение через ${Math.round(delay / 1000)}с...`, 'error')
        retryTimerRef.current = setTimeout(connect, delay)
      } else {
        setStatus('failed')
        toast('Не удалось подключиться к серверу. Попробуйте позже.', 'error')
      }
    }

    ws.onerror = () => {
      /* onclose handles recovery */
    }
    // flushPending & startHeartbeat are stable within a render and are only
    // called from the socket's own event handlers; adding them would recreate
    // `connect` and re-fire the connect effect, churning the WebSocket. The
    // ref-based design makes the omission safe (they always see live refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPolling])

  // Drain the offline outbox. Prefer the socket; if it's not open but we're back
  // online, HTTP-POST each queued message so it isn't stranded in degraded mode
  // (original flushed over both transports, connection.js:218-246).
  const flushPending = useCallback(async () => {
    if (!useMessagingStore.getState().pendingMessages.length) return
    while (useMessagingStore.getState().pendingMessages.length) {
      const { takePending, unshiftPending, addMessage, setTyping, clear, addPaywall, addLimitNotice } = useMessagingStore.getState()
      const content = takePending()
      if (content === undefined) break // queue drained under us; nothing to flush
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'message', content, content_type: 'text' }))
        pendingAcceptance.current += 1
        continue
      }
      if (!navigator.onLine) {
        unshiftPending(content)
        break
      }
      try {
        setTyping(true)
        const responses = await chatApi.send(content)
        if (isTextMessageAccepted(responses)) analytics.track('messageAccepted')
        setTyping(false)
        for (const msg of responses) {
          if (msg.metadata?.action === 'show_pricing') addPaywall()
          else if (msg.metadata?.action === 'show_limit_notice') addLimitNotice(msg.metadata.reset_at)
          else if (msg.metadata?.action === 'clear_chat') clear()
          else addMessage('assistant', msg.content, { contentType: msg.content_type || 'text', mediaUrl: msg.media_url || null, metadata: msg.metadata ?? null })
        }
      } catch {
        setTyping(false)
        unshiftPending(content) // keep it queued; stop and retry later
        break
      }
    }
  }, [])

  // send over the socket if open; return false so the caller can HTTP-fallback.
  const send = useCallback((content: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content, content_type: 'text' }))
      pendingAcceptance.current += 1
      return true
    }
    return false
  }, [])

  // Lifecycle: connect when enabled; tear down fully on unmount.
  useEffect(() => {
    if (!enabled) return
    closingRef.current = false
    connect()

    const onOnline = () => {
      toast('Подключение восстановлено', 'success')
      setStatus('connecting')
      retryRef.current = 0
      connect()
      flushPending()
    }
    const onOffline = () => {
      toast('Нет подключения к интернету', 'error')
      setStatus('offline')
    }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    if (!navigator.onLine) setStatus('offline')

    return () => {
      closingRef.current = true
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
      stopHeartbeat()
      stopPolling()
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [enabled, connect, flushPending])

  return { status, send }
}
